import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link, generatePath } from 'react-router-dom';
import { Paper } from '@mui/material';
import { groupBy } from 'lodash-es';

import { routes } from '../routes';
import { selectors as growerSelectors } from '../store/dux/grower-dux';
import { usePermissions } from '../hooks/use-permissions';
import { useFeatureFlags } from '../hooks/use-feature-flags';

export default function Home() {
  const permissions = usePermissions();
  const featureFlags = useFeatureFlags();
  const growerId = useSelector(growerSelectors.selectedId) || '';

  const groups = useMemo(() => {
    const visibleRoutes = routes.filter((route) => route.group);
    const grouped = Object.values(groupBy(visibleRoutes, 'group'));

    return grouped.map((groupRoutes) =>
      groupRoutes
        .filter(enforceGrowerRequirement)
        .filter(enforceFeatureFlag)
        .filter(enforceNegativeFeatureFlag)
        .filter(enforcePermission)
        .filter(enforceNegativePermission),
    );

    function enforceGrowerRequirement(route) {
      return !route.growerRequired || growerId;
    }

    function enforceFeatureFlag(route) {
      return !route.featureFlag || featureFlags.includes(route.featureFlag);
    }

    function enforceNegativeFeatureFlag(route) {
      return (
        !route.notFeatureFlag || !featureFlags.includes(route.notFeatureFlag)
      );
    }

    function enforcePermission(route) {
      if (!route.permission) return true;
      const required = [route.permission].flat();
      return required.some((permission) => permissions.includes(permission));
    }

    function enforceNegativePermission(route) {
      return !route.notPermission || !permissions.includes(route.notPermission);
    }
  }, [featureFlags, growerId, permissions]);

  return (
    <Paper
      sx={{
        padding: [3, 2],
        '& ul': {
          marginTop: 1,
          paddingLeft: 2,
          listStyle: 'none',
        },
      }}
    >
      {groups.map((routeGroup, groupIndex) => (
        <ul key={groupIndex}>
          {routeGroup.map((route, routeIndex) => (
            <li key={routeIndex}>
              <Link to={buildPath(route)}>
                {typeof route.name === 'function'
                  ? route.name(featureFlags)
                  : route.name}
              </Link>
            </li>
          ))}
        </ul>
      ))}
    </Paper>
  );

  function buildPath(route) {
    return generatePath(route.path, {
      year: new Date().getFullYear() - (new Date().getMonth() < 8 ? 1 : 0),
      growerid: growerId || null,
    });
  }
}
